import { useState } from 'react';
import { Loader2, Search, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function StatusIcon({ ok }) {
  return ok
    ? <CheckCircle className="h-4 w-4 text-green-600 inline" />
    : <XCircle className="h-4 w-4 text-red-500 inline" />;
}

function OwnerRow({ owner }) {
  return (
    <tr className="border-b last:border-b-0">
      <td className="py-1.5 pr-3 text-sm font-medium">{owner.canonical_name}</td>
      <td className="py-1.5 pr-3 text-sm font-mono">{owner.entity_id}</td>
      <td className="py-1.5 pr-3 text-sm text-center">
        <StatusIcon ok={owner.has_credential && owner.credential_active} />
      </td>
      <td className="py-1.5 pr-3 text-sm text-center">
        <StatusIcon ok={owner.has_api_key} />
      </td>
      <td className="py-1.5 pr-3 text-sm text-center">
        <StatusIcon ok={owner.fetchable} />
      </td>
      <td className="py-1.5 pr-3 text-sm text-right">{owner.txn_count_sample}</td>
      <td className="py-1.5 text-sm">
        {owner.is_invoicing_entity
          ? <Badge variant="outline" className="text-xs">invoicing entity</Badge>
          : owner.fetchable
            ? <Badge variant="default" className="text-xs bg-green-600">fetchable</Badge>
            : <Badge variant="destructive" className="text-xs">not connected</Badge>
        }
      </td>
    </tr>
  );
}

function RoutingRow({ r }) {
  const divergent = r.cash_receives_own_payouts && r.invoice_routed_to_bi_immo;
  return (
    <tr className="border-b last:border-b-0">
      <td className="py-1.5 pr-3 text-sm font-medium">{r.canonical_name}</td>
      <td className="py-1.5 pr-3 text-sm text-center">
        <StatusIcon ok={r.invoice_routed_to_bi_immo} />
      </td>
      <td className="py-1.5 pr-3 text-sm text-center">
        <StatusIcon ok={r.has_own_bank_feed} />
      </td>
      <td className="py-1.5 pr-3 text-sm">
        {divergent
          ? <Badge variant="secondary" className="text-xs">divergent (expected)</Badge>
          : r.entity_id === 'bi_immo'
            ? <Badge variant="outline" className="text-xs">self</Badge>
            : <Badge variant="destructive" className="text-xs">no bank feed</Badge>
        }
      </td>
      <td className="py-1.5 text-xs text-muted-foreground">{r.note}</td>
    </tr>
  );
}

function WorkedExample({ ex }) {
  if (!ex) return null;

  if (ex.status !== 'traced') {
    return (
      <div className="border rounded p-3 bg-muted/50">
        <p className="text-sm font-medium flex items-center gap-1">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          {ex.status}
        </p>
        <p className="text-sm text-muted-foreground mt-1">{ex.explanation}</p>
      </div>
    );
  }

  const inv = ex.invoice || {};
  const res = ex.reservation || {};
  const pay = ex.payout_search || {};

  return (
    <div className="border rounded p-3 space-y-3">
      <p className="text-sm font-semibold">End-to-end trace</p>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <div className="font-medium text-muted-foreground">Invoice</div>
        <div>{inv.number || inv.id} ({inv.date})</div>
        <div className="font-medium text-muted-foreground">Amount</div>
        <div>EUR {inv.amount}</div>
        <div className="font-medium text-muted-foreground">Customer</div>
        <div>{inv.customer}</div>
        <div className="font-medium text-muted-foreground">Label</div>
        <div className="truncate">{inv.label}</div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <div className="font-medium text-muted-foreground">Reservation</div>
        <div>{res.external_reservation_id || res.uplisting_id}</div>
        <div className="font-medium text-muted-foreground">Channel</div>
        <div>{res.channel}</div>
        <div className="font-medium text-muted-foreground">Property</div>
        <div>{res.property_name}</div>
        <div className="font-medium text-muted-foreground">Owner entity</div>
        <div className="font-semibold">{res.owner_name} ({res.owner_entity_id})</div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <div className="font-medium text-muted-foreground">Searched in</div>
        <div>{pay.searched_entity_name} ({pay.searched_entity})</div>
        <div className="font-medium text-muted-foreground">Transactions scanned</div>
        <div>{pay.txn_count_scanned}</div>
        <div className="font-medium text-muted-foreground">Payout found?</div>
        <div>
          {pay.match_found
            ? <Badge variant="default" className="text-xs bg-green-600">yes</Badge>
            : <Badge variant="destructive" className="text-xs">no</Badge>
          }
        </div>
        {pay.match_detail && (
          <>
            <div className="font-medium text-muted-foreground">Match date</div>
            <div>{pay.match_detail.txn_date}</div>
            <div className="font-medium text-muted-foreground">Match amount</div>
            <div>EUR {pay.match_detail.txn_amount} (diff: {pay.match_detail.amount_diff})</div>
            <div className="font-medium text-muted-foreground">Label</div>
            <div className="truncate">{pay.match_detail.txn_label}</div>
          </>
        )}
      </div>

      <div className="bg-muted/50 rounded p-2 text-sm italic">
        {ex.thesis}
      </div>
    </div>
  );
}

export function BiImmoReconScopePanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.biImmoReconScope();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const q1 = result?.q1_current_scope;
  const q2 = result?.q2_owner_entities || [];
  const q3 = result?.q3_fetchable_summary;
  const q4 = result?.q4_routing_agreement || [];
  const q5 = result?.q5_worked_example;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5 text-amber-600" />
          BI Immo Recon Scope Diagnostic
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Read-only analysis of the invoicing-entity vs cash-receiving-entity mismatch
          for Bonjour Immobilier. Reports current bank scope, owner entity connection
          status, invoice-vs-cash routing agreement, and an end-to-end payout trace.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Run Diagnostic
        </Button>

        {result && (
          <div className="space-y-6">
            {/* Q1: Current scope */}
            <div>
              <h3 className="text-sm font-semibold mb-1">Q1: Current bank scope</h3>
              <div className="bg-muted/50 rounded p-2 text-sm space-y-1">
                <div><span className="font-medium">Mechanism:</span> {q1?.scoping_mechanism}</div>
                <div><span className="font-medium">Single-entity:</span> {q1?.single_entity ? 'Yes' : 'No'}</div>
                <div><span className="font-medium">Key lines:</span> {q1?.scoping_file}:{q1?.credential_fetch_line}, :{q1?.api_key_resolve_line}, :{q1?.transaction_fetch_line}</div>
                <p className="text-muted-foreground text-xs mt-1">{q1?.explanation}</p>
              </div>
            </div>

            {/* Q2+Q3: Owner entities */}
            <div>
              <h3 className="text-sm font-semibold mb-1">
                Q2/Q3: Owner entities routed to BI Immo
                {q3 && (
                  <span className="ml-2 font-normal text-muted-foreground">
                    ({q3.fetchable}/{q3.total_owners} fetchable)
                  </span>
                )}
              </h3>
              {q3?.not_fetchable?.length > 0 && (
                <div className="text-xs text-red-600 mb-1">
                  Not fetchable: {q3.not_fetchable.join(', ')}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th className="py-1 pr-3">Entity</th>
                      <th className="py-1 pr-3">ID</th>
                      <th className="py-1 pr-3 text-center">Cred</th>
                      <th className="py-1 pr-3 text-center">Key</th>
                      <th className="py-1 pr-3 text-center">Fetch</th>
                      <th className="py-1 pr-3 text-right">Txns</th>
                      <th className="py-1">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q2.map(o => <OwnerRow key={o.entity_id} owner={o} />)}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Q4: Routing agreement */}
            <div>
              <h3 className="text-sm font-semibold mb-1">Q4: Cash routing vs invoice routing</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th className="py-1 pr-3">Entity</th>
                      <th className="py-1 pr-3 text-center">Invoice to BI Immo</th>
                      <th className="py-1 pr-3 text-center">Own bank feed</th>
                      <th className="py-1 pr-3">Agreement</th>
                      <th className="py-1">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q4.map(r => <RoutingRow key={r.entity_id} r={r} />)}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Q5: Worked example */}
            <div>
              <h3 className="text-sm font-semibold mb-1">Q5: Worked example (end-to-end trace)</h3>
              <WorkedExample ex={q5} />
            </div>

            {/* Fix note */}
            {result.fix_note && (
              <div className="border-l-4 border-amber-400 pl-3 py-1">
                <p className="text-xs font-medium text-amber-700">Fix-prompt note</p>
                <p className="text-xs text-muted-foreground">{result.fix_note}</p>
              </div>
            )}

            {/* Timestamp */}
            <p className="text-xs text-muted-foreground">
              Checked at: {result.checked_at}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
